import { env } from "@/env";
import { extractFrameAtTime } from "@/app/_utilities/videoUtils";
import { useCallback } from "react";

import type { ExtractedFrame, SoundtrackOptions, VideoProcessingResult } from "../types";

import { FRAME_TIMESTAMPS } from "../constants";

export const useVideoProcessing = () => {
  const extractFrames = useCallback(async (file: File): Promise<ExtractedFrame[]> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      const frames: ExtractedFrame[] = [];
      let currentFrameIndex = 0;
      let videoUrl: null | string = null;

      const cleanup = () => {
        if (videoUrl) {
          URL.revokeObjectURL(videoUrl);
          videoUrl = null;
        }
        video.removeEventListener("loadedmetadata", loadedHandler);
        video.removeEventListener("error", errorHandler);
        video.src = "";
      };

      const processNextFrame = async () => {
        if (currentFrameIndex >= FRAME_TIMESTAMPS.length) {
          cleanup();
          resolve(frames);
          return;
        }

        try {
          const timestamp = FRAME_TIMESTAMPS[currentFrameIndex];
          const dataURL = await extractFrameAtTime(video, timestamp);
          frames.push({ timestamp, dataURL });
          currentFrameIndex++;
          processNextFrame();
        } catch (err) {
          cleanup();
          reject(err);
        }
      };

      const loadedHandler = () => {
        processNextFrame();
      };

      const errorHandler = () => {
        cleanup();
        reject(new Error("Video loading failed"));
      };

      video.addEventListener("loadedmetadata", loadedHandler);
      video.addEventListener("error", errorHandler);

      videoUrl = URL.createObjectURL(file);
      video.src = videoUrl;
    });
  }, []);

  const processVideo = useCallback(
    async (videoFile: File, _soundtrackOptions: SoundtrackOptions): Promise<VideoProcessingResult> => {
      // Extract frames from video
      const frames = await extractFrames(videoFile);

      // Call the new marketing endpoint
      const response = await fetch(`${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/video/video-to-music-marketing`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          frames,
        }),
      });

      if (!response.ok) {
        let errorData: { error: string } | null = null;
        try {
          errorData = await response.json();
        } catch (error) {
          throw new Error("Failed to process video and generate music");
        }

        // Check if this is a rate limiting error (429) or quota limit error (401)
        if (response.status === 429 || response.status === 401) {
          const error = new Error(errorData?.error || "Rate limit exceeded") as Error & { isRateLimited: boolean };
          error.isRateLimited = true;
          throw error;
        }

        throw new Error(errorData?.error || "Failed to process video and generate music");
      }

      const result = await response.json();

      if (!result.success || !result.audioData) {
        throw new Error("Invalid response from server");
      }

      return {
        audioData: result.audioData,
        frameAnalyses: result.description || "",
      };
    },
    [extractFrames],
  );

  return {
    extractFrames,
    processVideo,
  };
};
